import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { featuredComics, latestComics, testimonials } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function getComicById(id: string): Comic | undefined {
  const allComics = [...featuredComics, ...latestComics];
  return allComics.find((c) => c.id === id);
}

function getRelatedComics(comic: Comic): Comic[] {
  const allComics = [...featuredComics, ...latestComics];
  return allComics.filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id).slice(0, 4);
}

export default function ComicDetailPage() {
  const { comicId } = useParams<{ comicId: string }>();
  const comic = getComicById(comicId || '');
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-foreground-600 text-lg">漫画未找到</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = getRelatedComics(comic);
  const pageTitle = `${comic.title}漫画_${comic.category}韩漫在线阅读-韩国漫画网`;
  const pageDesc = `${comic.title}：${comic.description} 作者${comic.author}，${comic.status}，最新章节${comic.latestChapter}。韩国漫画网提供高清免费韩漫在线观看。`;
  const currentTestimonial = testimonials[testimonialIndex % testimonials.length];

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${comic.title},${comic.category}韩漫,韩国漫画,免费韩漫,韩漫在线观看`} />
      <link rel="canonical" href={`http://acwaterpump.com/comic/${comic.id}`} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={`http://acwaterpump.com/comic/${comic.id}`} />
      <meta property="og:image" content={comic.coverImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={comic.coverImage} />

      {/* Article JSON-LD */}
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: `${comic.title} - ${comic.category}韩国漫画`,
        description: pageDesc,
        image: comic.coverImage,
        author: { '@type': 'Person', name: comic.author },
        datePublished: '2026-01-01',
        dateModified: comic.updateDate,
        publisher: { '@type': 'Organization', name: '韩国漫画网', url: 'http://acwaterpump.com/' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': `http://acwaterpump.com/comic/${comic.id}` },
      })}</script>

      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: comic.category, path: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]} />

          {/* Comic Detail Header */}
          <section className="flex flex-col lg:flex-row gap-10 py-10 md:py-16">
            {/* Left - cover */}
            <div className="w-full lg:w-1/3">
              <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-300 text-foreground-700 text-xs font-medium mb-4">
                {comic.status}
              </span>
              <div className="rounded-3xl overflow-hidden aspect-[3/4]">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title}漫画封面`}
                  title={`${comic.title} - 免费韩漫大全`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Right - info */}
            <div className="w-full lg:w-2/3 flex flex-col justify-center">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                {comic.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {comic.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i> {comic.rating}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {comic.views.toLocaleString()}
                </span>
                <span>{comic.latestChapter}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-foreground-600 text-sm md:text-base leading-relaxed max-w-xl">
                {comic.description}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button className="whitespace-nowrap px-8 py-3.5 rounded-xl bg-foreground-950 text-background-50 font-medium text-sm cursor-pointer hover:bg-foreground-800 transition-colors inline-flex items-center gap-2">
                  <i className="ri-play-circle-line text-lg"></i>
                  开始阅读
                </button>
                <button className="whitespace-nowrap px-8 py-3.5 rounded-xl border border-foreground-300 text-foreground-700 font-medium text-sm cursor-pointer hover:border-foreground-500 transition-colors inline-flex items-center gap-2">
                  <i className="ri-bookmark-line text-lg"></i>
                  加入收藏
                </button>
              </div>
            </div>
          </section>

          {/* Chapter List */}
          <section className="pb-10">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">章节列表</h2>
            <div className="flex flex-col gap-2 max-w-2xl">
              {comic.chapters.map((ch) => (
                <div
                  key={ch.number}
                  className="flex items-center justify-between px-5 py-3.5 rounded-xl bg-background-50 border border-background-300/60 cursor-pointer hover:border-primary-300 transition-colors"
                >
                  <span className="text-sm font-medium text-foreground-950">
                    第{ch.number}话 {ch.title}
                  </span>
                  <span className="text-xs text-foreground-500">{ch.updateDate}</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Testimonial Section */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-[1440px] mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              {/* Left - image */}
              <div className="w-full lg:w-[40%]">
                <div className="rounded-[32px] overflow-hidden bg-secondary-300 aspect-square">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20portrait%20of%20Korean%20webtoon%20artist%20at%20work%2C%20drawing%20tablet%20with%20illustration%2C%20warm%20studio%20lighting%2C%20creative%20atmosphere%2C%20soft%20natural%20light%2C%20professional%20creative%20workspace%2C%20minimalist%20decor%2C%20artistic%20vibe&width=800&height=800&seq=testimonial-comic-01&orientation=squarish"
                    alt="编辑推荐"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Right - review */}
              <div className="w-full lg:w-[60%] flex flex-col justify-center">
                <span className="text-xs text-foreground-500 font-medium tracking-wide">
                  （编辑推荐）
                </span>
                <h2 className="mt-2 font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
                  读者口碑
                  <br />
                  <span className="text-foreground-400">真实评价</span>
                </h2>
                <blockquote className="mt-10 md:mt-12 text-foreground-700 text-base md:text-lg leading-relaxed">
                  &ldquo;{currentTestimonial.quote}&rdquo;
                </blockquote>
                <p className="mt-6 text-foreground-950 font-semibold text-sm md:text-base">
                  &mdash; {currentTestimonial.author}
                </p>
                <p className="text-xs text-foreground-500">{currentTestimonial.role}</p>

                {/* Navigation arrows */}
                <div className="mt-8 flex items-center gap-4">
                  <button
                    onClick={() => setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-foreground-300 text-foreground-600 cursor-pointer hover:border-foreground-500 transition-colors"
                    aria-label="上一条评价"
                  >
                    <i className="ri-arrow-left-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => setTestimonialIndex((prev) => (prev + 1) % testimonials.length)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 cursor-pointer hover:bg-foreground-800 transition-colors"
                    aria-label="下一条评价"
                  >
                    <i className="ri-arrow-right-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Comics */}
        <section className="py-16 md:py-24 max-w-[1440px] mx-auto px-6 md:px-10">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-10">
            相关作品推荐
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {relatedComics.map((rc) => (
              <Link
                key={rc.id}
                to={`/comic/${rc.id}`}
                className="group cursor-pointer"
                data-product-shop=""
              >
                <div className="rounded-xl overflow-hidden bg-background-50">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={rc.coverImage}
                      alt={rc.title}
                      title={`${rc.title}漫画 - 韩漫在线观看`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 whitespace-nowrap overflow-hidden text-ellipsis">
                      {rc.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">{rc.latestChapter}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}